import { createPortal } from 'react-dom';
import { AnimatePresence, m, useDragControls, type PanInfo } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { DUR, EASE_IOS, EASE_SALIDA } from '@/lib/motion';

// Cierre por arrastre: distancia o velocidad hacia abajo
const CIERRE_OFFSET_PX = 120;
const CIERRE_VELOCIDAD = 500;

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** 'adaptive' = content height (max 85vh); 'large' = 92vh for long forms */
  detent?: 'adaptive' | 'large';
  className?: string;
  /** Hide the drag pill and close button — used for action sheets */
  minimal?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  detent = 'adaptive',
  className,
  minimal = false,
}: BottomSheetProps) {
  const dragControls = useDragControls();
  // Se libera al empezar la salida (no al terminarla) para que el scroll
  // de la página vuelva a su lugar sin salto.
  useBodyScrollLock(open);

  const alSoltar = (_: unknown, info: PanInfo) => {
    if (info.offset.y > CIERRE_OFFSET_PX || info.velocity.y > CIERRE_VELOCIDAD) onClose();
  };
  // El arrastre arranca solo desde la barra y el encabezado, así no compite
  // con el scroll del contenido.
  const iniciarArrastre = (e: React.PointerEvent) => {
    if (minimal || (e.target as HTMLElement).closest('button')) return;
    dragControls.start(e);
  };

  return createPortal(
    <AnimatePresence>
    {open && (
    <m.div
      key="sheet-overlay"
      className="fixed inset-0 z-[9999] flex items-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: DUR.base } }}
      exit={{ opacity: 0, transition: { duration: DUR.base, ease: EASE_SALIDA } }}
      style={{
        backgroundColor: 'rgba(0, 30, 64, 0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onClick={onClose}
    >
      <m.div
        className={cn(
          'relative w-full bg-white flex flex-col overflow-hidden',
          'rounded-t-[24px]',
          detent === 'large' ? 'max-h-[92vh]' : 'max-h-[85vh]',
          className,
        )}
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: DUR.sheet, ease: EASE_IOS } }}
        exit={{ y: '100%', transition: { duration: DUR.base, ease: EASE_SALIDA } }}
        drag={minimal ? false : 'y'}
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.7 }}
        onDragEnd={alSoltar}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Barra de arrastre (deslizar hacia abajo para cerrar) */}
        {!minimal && (
          <div
            className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab select-none"
            style={{ touchAction: 'none' }}
            onPointerDown={iniciarArrastre}
          >
            <div className="w-10 h-[5px] rounded-full bg-gray-200" />
          </div>
        )}

        {/* Header */}
        {(title || !minimal) && title && (
          <div
            className="flex items-center gap-3 px-5 py-3 shrink-0 select-none"
            style={{ borderBottom: '1px solid rgba(60,60,67,0.13)', touchAction: 'none' }}
            onPointerDown={iniciarArrastre}
          >
            {icon && (
              <div
                className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
                style={{ backgroundColor: '#001e40' }}
              >
                <span style={{ color: '#fed65b' }}>{icon}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-ios-headline text-sgcc-primary truncate">{title}</p>
              {subtitle && (
                <p className="text-ios-caption1 text-sgcc-on-surface-variant truncate mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 tap-active"
              style={{ backgroundColor: 'rgba(60,60,67,0.1)' }}
              aria-label="Cerrar"
            >
              <X size={16} strokeWidth={2.5} style={{ color: '#43474f' }} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>

        {/* Footer */}
        {footer && (
          <div
            className="shrink-0 px-4 pt-3 pb-4"
            style={{ borderTop: '1px solid rgba(60,60,67,0.1)' }}
          >
            {footer}
          </div>
        )}
      </m.div>
    </m.div>
    )}
    </AnimatePresence>,
    document.body,
  );
}
